import React, { useRef } from 'react';
import { Modal, Button, Alert, Typography } from 'antd';
import { QrcodeOutlined, DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import env from '../../env';

const { Title, Text } = Typography;

const AuctionQRCodeModal = ({ 
    visible, 
    onClose, 
    auction,
    qrValue = null, // Optional: custom QR value, defaults to auction URL
    title = 'Auction QR Code',
    subtitle = null, // Optional: custom subtitle, defaults to auction title
    showInfoAlert = true,
    infoMessage = 'This QR code is for promoting your auction on social media or via messaging apps. This is NOT an auction joining link.',
    downloadFileName = null // Optional: custom download filename
}) => {
    const qrRef = useRef(null);

    const downloadQRCode = () => {
        if (qrRef.current) {
            const canvas = qrRef.current.querySelector('canvas');
            if (canvas) {
                const url = canvas.toDataURL('image/png');
                const link = document.createElement('a');
                const fileName = downloadFileName || `auction-qr-${auction?.slug || 'auction'}.png`;
                link.download = fileName;
                link.href = url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };

    // Determine QR code value
    const getQRValue = () => {
        if (qrValue) {
            return qrValue;
        }
        if (auction?.slug) {
            return `${env.FRONTEND_URL}/auctions/${auction.slug}`;
        }
        return '';
    };

    // Get subtitle text
    const getSubtitle = () => {
        if (subtitle !== null) {
            return subtitle;
        }
        return auction?.title || '';
    };

    if (!auction && !qrValue) {
        return null;
    }

    return (
        <>
            <Modal
                title={null}
                open={visible}
                onCancel={onClose}
                footer={null}
                centered
                width={550}
                closable={true}
                maskClosable={true}
                className="qr-code-modal"
                style={{ padding: 0 }}
            >
                <div style={{ 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    {/* Header Section */}
                    <div style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        padding: '20px 24px 16px 24px',
                        textAlign: 'center',
                        borderBottom: 'none'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 12px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                            <QrcodeOutlined style={{ fontSize: '24px', color: 'white' }} />
                        </div>
                        <Title level={4} style={{ 
                            color: 'white', 
                            margin: 0,
                            fontWeight: 600,
                            fontSize: '20px'
                        }}>
                            {title}
                        </Title>
                        {getSubtitle() && (
                            <Text style={{ 
                                color: 'rgba(255, 255, 255, 0.9)', 
                                fontSize: '13px',
                                display: 'block',
                                marginTop: '6px'
                            }}>
                                {getSubtitle()}
                            </Text>
                        )}
                    </div>

                    {/* Content Section */}
                    <div style={{ padding: '24px' }} ref={qrRef}>
                        <div style={{ 
                            textAlign: 'center',
                            padding: '20px',
                            background: 'white',
                            borderRadius: '12px',
                            border: '1px solid #e6f7ff',
                            marginBottom: '20px'
                        }}>
                            <QRCodeCanvas 
                                value={getQRValue()}
                                size={240}
                                level="H"
                                includeMargin={true}
                            />
                        </div>
                        
                        {showInfoAlert && (
                            <Alert
                                message={<Text strong style={{ fontSize: '14px' }}>Promotional QR Code</Text>}
                                description={
                                    <Text style={{ fontSize: '13px', display: 'block', marginTop: 4 }}>
                                        {infoMessage}
                                    </Text>
                                }
                                type="info"
                                showIcon
                                icon={<InfoCircleOutlined />}
                                style={{ 
                                    marginBottom: '20px',
                                    borderRadius: '8px',
                                    border: '1px solid #91d5ff'
                                }}
                            />
                        )}

                        {/* Footer Actions */}
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '12px',
                            paddingTop: '16px',
                            borderTop: '1px solid #f0f0f0'
                        }}>
                            <Button 
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={downloadQRCode}
                                size="large"
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    padding: '0 24px',
                                    height: '40px',
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    border: 'none'
                                }}
                            >
                                Download QR Code
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal Styles */}
            <style>{`
                .qr-code-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .qr-code-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .qr-code-modal .ant-modal-close:hover {
                    color: white;
                }
            `}</style>
        </>
    );
};

export default AuctionQRCodeModal;

